import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ShoppingBag, Loader2, TrendingUp, AlertCircle, CheckCircle2, Clock
} from 'lucide-react';
import api from "../../../api/axios";
import { Link } from 'react-router-dom';

const DashboardHome = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get("/dashboard/stats");
        if (res.data.success) {
          setData(res.data);
        }
      } catch (err) {
        console.error("Dashboard error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <Loader2 className="animate-spin text-[#ffaa00]" size={40} />
      <p className="text-xs font-black uppercase tracking-widest text-gray-400">Analyse Pro en cours...</p>
    </div>
  );

  const stats = data.stats;

  const proCards = [
    { 
      label: "Revenu Encaissé", 
      value: `${parseFloat(stats.actual_revenue).toLocaleString()} DH`, 
      sub: "Commandes Livrées",
      icon: <CheckCircle2 size={22} />,
      color: "bg-green-500",
      textColor: "text-green-600"
    },
    { 
      label: "En Attente", 
      value: `${parseFloat(stats.potential_revenue).toLocaleString()} DH`, 
      sub: "Pending / Processing",
      icon: <Clock size={22} />,
      color: "bg-[#ffaa00]",
      textColor: "text-orange-600"
    },
    { 
      label: "Volume Commandes", 
      value: stats.orders_count, 
      sub: "Total historique",
      icon: <ShoppingBag size={22} />,
      color: "bg-black",
      textColor: "text-gray-900"
    },
    { 
      label: "Alertes Stock", 
      value: stats.out_of_stock, 
      sub: `${stats.products_count} produits total`,
      icon: <AlertCircle size={22} />,
      color: stats.out_of_stock > 0 ? "bg-red-500" : "bg-blue-500",
      textColor: stats.out_of_stock > 0 ? "text-red-600" : "text-blue-600"
    },
  ];

  const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered': return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      case 'processing': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-orange-100 text-orange-700 border-orange-200';
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-10 p-2">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-tighter text-gray-900">Console Admin</h1>
          <p className="text-xs font-bold text-[#8A8A89] mt-1 uppercase tracking-[0.2em] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Statistiques Temps Réel
          </p>
        </div>

      </div>

      {/* PRO STATS GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {proCards.map((stat, index) => (
          <div key={index} className="bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group">
            <div className={`p-3 w-fit rounded-2xl text-white shadow-lg mb-4 transition-transform group-hover:scale-110 ${stat.color}`}>
              {stat.icon}
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">{stat.label}</p>
            <h3 className="text-2xl font-black text-gray-900 mt-1">{stat.value}</h3>
            <p className={`text-[10px] font-bold mt-2 uppercase ${stat.textColor}`}>{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* RECENT ORDERS SECTION */}
      <div className="bg-white rounded-[3rem] border border-gray-100 shadow-xl overflow-hidden">
        <div className="p-8 border-b border-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-orange-50 p-2 rounded-xl text-[#ffaa00]">
              <TrendingUp size={20} />
            </div>
            <h2 className="text-xl font-black uppercase tracking-tight">Flux des Commandes</h2>
          </div>
          <Link to={"./orders"}>
            <button className="px-5 py-2.5 bg-gray-50 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-500 hover:bg-black hover:text-white transition-all">
              Journal Complet
            </button>
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Référence</th>
                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Client</th>
                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Montant Total</th>
                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">État du Flux</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.recent_orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50/40 transition-colors group">
                  <td className="px-8 py-6">
                    <span className="text-xs font-black text-gray-400 group-hover:text-black transition-colors">
                      #{order.order_number}
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    <div className="flex flex-col">
                      <span className="text-sm font-black text-gray-900">{order.full_name}</span>
                      <span className="text-[10px] text-gray-400 font-bold uppercase">{order.city}</span>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <span className="text-sm font-black text-[#ffaa00]">
                      {parseFloat(order.total_amount).toFixed(2)} DH
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    <span className={`px-4 py-1.5 rounded-xl text-[9px] uppercase font-black border ${getStatusStyle(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.recent_orders.length === 0 && (
            <div className="p-20 text-center">
              <ShoppingBag className="mx-auto text-gray-100 mb-4" size={60} />
              <p className="text-gray-400 font-black uppercase tracking-widest text-xs">En attente de votre première vente...</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardHome;